import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Form,
  InputNumber,
  Input,
  Button,
  Select,
  Row,
  Col,
  Typography,
  Space,
  Divider,
  Switch,
  Tag,
  Statistic,
  message,
  Tabs,
  Modal,
  Empty,
  Spin
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  CalculatorOutlined,
  SaveOutlined,
  HistoryOutlined,
  SettingOutlined,
  CarOutlined,
  RocketOutlined,
  NodeIndexOutlined,
  InfoCircleOutlined,
  EditOutlined
} from '@ant-design/icons';
import { DataTable, MoneyCell } from '../../ui';
import {
  useGetRailwayTariffsQuery,
  useGetExpeditionTariffsQuery,
  useCreateCalculationMutation,
  useUpdateCalculationMutation,
  useGetCalculationsQuery,
  useGetCalculationByIdQuery,
  useSeedTariffsMutation,
  useCreateRailwayTariffMutation,
  useUpdateRailwayTariffMutation,
  useDeleteRailwayTariffMutation,
  useCreateExpeditionTariffMutation,
  useUpdateExpeditionTariffMutation,
  RailwayTariff,
  ExpeditionTariff,
  DeliveryCostCalculation
} from '../../store/api/deliveryCostApi';
import { useGetAllContractorsQuery } from '../../store/api/referencesApi';
import { useGetApplicationsQuery, useGetApplicationByIdQuery } from '../../store/api/applicationsApi';
import { formatApplicationSelectOptionLabel } from '../../utils/applicationDisplayFormat';
const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface SegmentFormData {
  key: string;
  transportType: 'AUTO' | 'RAILWAY' | 'AIR';
  fromPoint: string;
  toPoint: string;
  wagonType?: string;
  pricePerKg?: number;
  pricePerM3?: number;
  segmentCost: number;
  clientRate?: number;
  manualCost: boolean;
  notes?: string;
}

const DeliveryCostCalculatorPage: React.FC = () => {
  const [form] = Form.useForm();
  const [tariffForm] = Form.useForm();
  const [expeditionForm] = Form.useForm();
  
  // Состояние
  const [segments, setSegments] = useState<SegmentFormData[]>([]);
  const [includeExpedition, setIncludeExpedition] = useState(false);
  const [customExpeditionCost, setCustomExpeditionCost] = useState<number | null>(null);
  const [calculatedExpeditionCost, setCalculatedExpeditionCost] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [totalClientRate, setTotalClientRate] = useState(0);
  const [activeTab, setActiveTab] = useState('calculator');
  const [tariffModalVisible, setTariffModalVisible] = useState(false);
  const [expeditionModalVisible, setExpeditionModalVisible] = useState(false);
  const [editingTariff, setEditingTariff] = useState<RailwayTariff | null>(null);
  const [editingExpedition, setEditingExpedition] = useState<ExpeditionTariff | null>(null);
  const [selectedApplicationId, setSelectedApplicationId] = useState<number | undefined>(undefined);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedCalculation, setSelectedCalculation] = useState<DeliveryCostCalculation | null>(null);
  const [selectedCalculationId, setSelectedCalculationId] = useState<number | undefined>(undefined);
  const [editingCalculationId, setEditingCalculationId] = useState<number | undefined>(undefined);
  
  const { data: detailedCalculation } = useGetCalculationByIdQuery(selectedCalculationId!, { skip: !selectedCalculationId });
  const { data: editingCalculation } = useGetCalculationByIdQuery(editingCalculationId!, { skip: !editingCalculationId });

  // API запросы
  const { data: railwayTariffs = [], isLoading: isLoadingTariffs, refetch: refetchTariffs } = useGetRailwayTariffsQuery();
  const { data: expeditionTariffs = [], isLoading: isLoadingExpedition } = useGetExpeditionTariffsQuery();
  const { data: contractors = [] } = useGetAllContractorsQuery();
  const { data: calculationsData, isLoading: isLoadingCalculations } = useGetCalculationsQuery({ page: 1, limit: 20 });
  const { data: applicationsData } = useGetApplicationsQuery({ page: 1, limit: 100, statusGroup: 'in_work' });
  const { data: selectedApplication } = useGetApplicationByIdQuery(selectedApplicationId!, { skip: !selectedApplicationId });
  
  const [createCalculation, { isLoading: isCreating }] = useCreateCalculationMutation();
  const [updateCalculation, { isLoading: isUpdating }] = useUpdateCalculationMutation();
  const [seedTariffs, { isLoading: isSeeding }] = useSeedTariffsMutation();
  const [createRailwayTariff] = useCreateRailwayTariffMutation();
  const [updateRailwayTariff] = useUpdateRailwayTariffMutation();
  const [deleteRailwayTariff] = useDeleteRailwayTariffMutation();
  const [createExpeditionTariff] = useCreateExpeditionTariffMutation();
  const [updateExpeditionTariff] = useUpdateExpeditionTariffMutation();

  const clients = contractors.filter((c: any) => c.roles?.includes('client'));

  // Уникальные станции из тарифов ЖД
  const uniqueFromStations = Array.from(new Set(railwayTariffs.map(t => t.fromStation))).sort();
  const uniqueToStations = Array.from(new Set(railwayTariffs.map(t => t.toStation))).sort();
  const allStations = Array.from(new Set(uniqueFromStations.concat(uniqueToStations))).sort();

  // Расчёт стоимости экспедирования
  useEffect(() => {
    const weight = form.getFieldValue('cargoWeightKg') || 0;
    if (includeExpedition && !customExpeditionCost) {
      const tariff = expeditionTariffs.find(t => {
        const min = Number(t.minWeightKg);
        const max = t.maxWeightKg ? Number(t.maxWeightKg) : Infinity;
        return weight >= min && weight < max;
      });
      setCalculatedExpeditionCost(tariff ? Number(tariff.price) : 0);
    }
  }, [form, includeExpedition, customExpeditionCost, expeditionTariffs]);

  // Пересчёт итогов
  useEffect(() => {
    const segmentsCost = segments.reduce((sum, s) => {
      const cost = Number(s.segmentCost) || 0;
      return sum + cost;
    }, 0);
    const clientRates = segments.reduce((sum, s) => {
      const rate = Number(s.clientRate) || Number(s.segmentCost) || 0;
      return sum + rate;
    }, 0);
    const expedition = includeExpedition ? (Number(customExpeditionCost) || Number(calculatedExpeditionCost) || 0) : 0;
    
    setTotalCost(segmentsCost + expedition);
    setTotalClientRate(clientRates + expedition);
  }, [segments, includeExpedition, customExpeditionCost, calculatedExpeditionCost]);

  // Загрузить данные расчёта в форму для редактирования
  const loadCalculationForEdit = useCallback((calculation: DeliveryCostCalculation) => {
    form.setFieldsValue({
      cargoPlaces: Number(calculation.cargoPlaces),
      cargoWeightKg: Number(calculation.cargoWeightKg),
      cargoVolumeM3: Number(calculation.cargoVolumeM3),
      cargoLengthM: calculation.cargoLengthM ? Number(calculation.cargoLengthM) : undefined,
      cargoWidthM: calculation.cargoWidthM ? Number(calculation.cargoWidthM) : undefined,
      cargoHeightM: calculation.cargoHeightM ? Number(calculation.cargoHeightM) : undefined,
      cargoDescription: calculation.cargoDescription || undefined,
      clientId: calculation.clientId || undefined
    });

    // Загружаем сегменты с явным преобразованием в числа
    const loadedSegments: SegmentFormData[] = calculation.segments.map((seg, idx) => ({
      key: `segment-${Date.now()}-${idx}`,
      transportType: seg.transportType,
      fromPoint: seg.fromPoint,
      toPoint: seg.toPoint,
      wagonType: seg.wagonType || undefined,
      pricePerKg: seg.pricePerKg ? Number(seg.pricePerKg) : undefined,
      pricePerM3: seg.pricePerM3 ? Number(seg.pricePerM3) : undefined,
      segmentCost: Number(seg.segmentCost),
      clientRate: seg.clientRate ? Number(seg.clientRate) : undefined,
      manualCost: seg.manualCost || false,
      notes: seg.notes || undefined
    }));
    setSegments(loadedSegments);

    // Загружаем экспедирование
    setIncludeExpedition(!!calculation.expeditionCost);
    if (calculation.expeditionCost) {
      setCustomExpeditionCost(Number(calculation.expeditionCost));
    } else {
      setCustomExpeditionCost(null);
    }

    // Переключаемся на вкладку калькулятора
    setActiveTab('calculator');
  }, [form]);

  // Загрузка данных расчёта для редактирования
  useEffect(() => {
    if (editingCalculation) {
      loadCalculationForEdit(editingCalculation);
    }
  }, [editingCalculation, loadCalculationForEdit]);

  // Автозаполнение полей при выборе заявки
  useEffect(() => {
    if (selectedApplication) {
      // Заполняем клиента
      if (selectedApplication.clientId) {
        form.setFieldsValue({ clientId: selectedApplication.clientId });
      }

      // Определяем точки погрузки и выгрузки из заявки
      // Приоритет: loadingAddress/unloadingAddress, затем адреса из shipper/consignee
      const loadingAddress = selectedApplication.loadingAddress || 
                            selectedApplication.shipper?.address || 
                            selectedApplication.shipper?.name || '';
      const unloadingAddress = selectedApplication.unloadingAddress || 
                               selectedApplication.consignee?.address || 
                               selectedApplication.consignee?.name || '';

      if (loadingAddress && unloadingAddress) {
        // Проверяем, есть ли уже сегмент с такими точками
        const hasSegment = segments.some(s => 
          s.fromPoint === loadingAddress && 
          s.toPoint === unloadingAddress
        );

        if (!hasSegment) {
          // Создаём новый сегмент с точками из заявки
          const newSegment: SegmentFormData = {
            key: `segment-${Date.now()}`,
            transportType: 'AUTO',
            fromPoint: loadingAddress,
            toPoint: unloadingAddress,
            segmentCost: 0,
            manualCost: true
          };
          setSegments(prev => [...prev, newSegment]);
        }
      }
    }
  }, [selectedApplication, form]);

  // Добавить сегмент маршрута
  const addSegment = () => {
    const newSegment: SegmentFormData = {
      key: `segment-${Date.now()}`,
      transportType: 'AUTO',
      fromPoint: '',
      toPoint: '',
      segmentCost: 0,
      manualCost: true
    };
    setSegments([...segments, newSegment]);
  };

  // Удалить сегмент
  const removeSegment = (key: string) => {
    setSegments(segments.filter(s => s.key !== key));
  };

  // Обновить сегмент
  const updateSegment = (key: string, field: string, value: any) => {
    setSegments(segments.map(s => {
      if (s.key !== key) return s;
      
      // Преобразуем числовые поля в числа
      let processedValue = value;
      if (field === 'segmentCost' || field === 'clientRate' || field === 'pricePerKg' || field === 'pricePerM3') {
        processedValue = value !== null && value !== undefined && value !== '' ? Number(value) : undefined;
      }
      
      const updated = { ...s, [field]: processedValue };
      
      // Автоподстановка тарифов ЖД
      if (updated.transportType === 'RAILWAY' && (field === 'fromPoint' || field === 'toPoint' || field === 'wagonType')) {
        const tariff = findMatchingTariff(updated.fromPoint, updated.toPoint, updated.wagonType);
        if (tariff) {
          updated.pricePerKg = Number(tariff.pricePerKg);
          updated.pricePerM3 = Number(tariff.pricePerM3);
          updated.manualCost = false;
          // Пересчёт стоимости
          const weight = Number(form.getFieldValue('cargoWeightKg')) || 0;
          const volume = Number(form.getFieldValue('cargoVolumeM3')) || 0;
          const costByWeight = weight * updated.pricePerKg;
          const costByVolume = volume * updated.pricePerM3;
          updated.segmentCost = Math.max(costByWeight, costByVolume);
        }
      }
      
      // Если изменились тарифы вручную - пересчитать
      if ((field === 'pricePerKg' || field === 'pricePerM3') && updated.transportType === 'RAILWAY') {
        const weight = Number(form.getFieldValue('cargoWeightKg')) || 0;
        const volume = Number(form.getFieldValue('cargoVolumeM3')) || 0;
        const costByWeight = weight * (Number(updated.pricePerKg) || 0);
        const costByVolume = volume * (Number(updated.pricePerM3) || 0);
        updated.segmentCost = Math.max(costByWeight, costByVolume);
      }
      
      return updated;
    }));
  };

  // Найти подходящий ЖД тариф (точное совпадение станций)
  const findMatchingTariff = (from: string, to: string, wagonType?: string): RailwayTariff | undefined => {
    if (!from || !to) return undefined;
    
    const weight = form.getFieldValue('cargoWeightKg') || 0;
    
    // Ищем точные совпадения
    const matching = railwayTariffs.filter(t => 
      t.fromStation.toLowerCase() === from.toLowerCase() && 
      t.toStation.toLowerCase() === to.toLowerCase()
    );

    if (matching.length === 0) return undefined;

    // Если указан тип вагона DETACHED и вес достаточный
    if (wagonType === 'DETACHED') {
      const detached = matching.find(t => 
        t.wagonType === 'DETACHED' && 
        (!t.minWeightKg || weight >= Number(t.minWeightKg))
      );
      if (detached) return detached;
    }

    // Иначе ищем PASSING или без типа
    return matching.find(t => t.wagonType === 'PASSING' || !t.wagonType) || matching[0];
  };

  // Пересчитать все сегменты при изменении веса/объёма
  const recalculateSegments = () => {
    const weight = Number(form.getFieldValue('cargoWeightKg')) || 0;
    const volume = Number(form.getFieldValue('cargoVolumeM3')) || 0;

    setSegments(segments.map(s => {
      if (s.transportType !== 'RAILWAY' || s.manualCost) return s;
      
      const costByWeight = weight * (Number(s.pricePerKg) || 0);
      const costByVolume = volume * (Number(s.pricePerM3) || 0);
      return { ...s, segmentCost: Math.max(costByWeight, costByVolume) };
    }));
  };

  // Сохранить расчёт
  const handleSaveCalculation = async () => {
    try {
      const values = await form.validateFields();
      
      if (segments.length === 0) {
        message.warning('Добавьте хотя бы один сегмент маршрута');
        return;
      }

      const calculationData = {
        cargoPlaces: values.cargoPlaces || 1,
        cargoWeightKg: values.cargoWeightKg,
        cargoVolumeM3: values.cargoVolumeM3,
        cargoLengthM: values.cargoLengthM,
        cargoWidthM: values.cargoWidthM,
        cargoHeightM: values.cargoHeightM,
        cargoDescription: values.cargoDescription,
        segments: segments.map((s, idx) => ({
          transportType: s.transportType,
          fromPoint: s.fromPoint,
          toPoint: s.toPoint,
          wagonType: s.wagonType,
          pricePerKg: s.pricePerKg,
          pricePerM3: s.pricePerM3,
          segmentCost: s.segmentCost,
          clientRate: s.clientRate,
          manualCost: s.manualCost,
          sortOrder: idx,
          notes: s.notes
        })),
        includeExpedition,
        customExpeditionCost: customExpeditionCost || undefined,
        clientId: values.clientId,
        applicationId: selectedApplicationId
      };

      if (editingCalculationId) {
        // Обновляем существующий расчёт
        await updateCalculation({
          id: editingCalculationId,
          ...calculationData
        }).unwrap();
        message.success('Расчёт обновлён');
        setEditingCalculationId(undefined);
      } else {
        // Создаём новый расчёт
        await createCalculation(calculationData).unwrap();
        message.success('Расчёт сохранён');
      }

      // Очищаем форму
      form.resetFields();
      setSegments([]);
      setIncludeExpedition(false);
      setCustomExpeditionCost(null);
      setSelectedApplicationId(undefined);
    } catch (error) {
      message.error(editingCalculationId ? 'Ошибка обновления расчёта' : 'Ошибка сохранения расчёта');
    }
  };

  // Инициализация тарифов
  const handleSeedTariffs = async () => {
    try {
      await seedTariffs().unwrap();
      message.success('Тарифы загружены');
      refetchTariffs();
    } catch (error) {
      message.error('Ошибка загрузки тарифов');
    }
  };

  // Сохранить/обновить ЖД тариф
  const handleSaveTariff = async () => {
    try {
      const values = await tariffForm.validateFields();
      if (editingTariff) {
        await updateRailwayTariff({ id: editingTariff.id, ...values }).unwrap();
        message.success('Тариф обновлён');
      } else {
        await createRailwayTariff(values).unwrap();
        message.success('Тариф создан');
      }
      setTariffModalVisible(false);
      tariffForm.resetFields();
      setEditingTariff(null);
    } catch (error) {
      message.error('Ошибка сохранения тарифа');
    }
  };

  // Сохранить/обновить тариф экспедирования
  const handleSaveExpeditionTariff = async () => {
    try {
      const values = await expeditionForm.validateFields();
      if (editingExpedition) {
        await updateExpeditionTariff({ id: editingExpedition.id, ...values }).unwrap();
        message.success('Тариф обновлён');
      } else {
        await createExpeditionTariff(values).unwrap();
        message.success('Тариф создан');
      }
      setExpeditionModalVisible(false);
      expeditionForm.resetFields();
      setEditingExpedition(null);
    } catch (error) {
      message.error('Ошибка сохранения тарифа');
    }
  };

  // Иконка типа транспорта
  const getTransportIcon = (type: string) => {
    switch (type) {
      case 'AUTO': return <CarOutlined />;
      case 'RAILWAY': return <NodeIndexOutlined />;
      case 'AIR': return <RocketOutlined />;
      default: return <CarOutlined />;
    }
  };

  // Получить информацию о расчёте стоимости ЖД
  const getCalculationInfo = (segment: SegmentFormData) => {
    const weight = form.getFieldValue('cargoWeightKg') || 0;
    const volume = form.getFieldValue('cargoVolumeM3') || 0;
    const costByWeight = weight * (segment.pricePerKg || 0);
    const costByVolume = volume * (segment.pricePerM3 || 0);
    
    if (costByWeight > costByVolume) {
      return { type: 'weight', text: `по весу: ${weight} кг × ${segment.pricePerKg} ₽`, cost: costByWeight };
    } else {
      return { type: 'volume', text: `по объёму: ${volume} м³ × ${segment.pricePerM3} ₽`, cost: costByVolume };
    }
  };

  // Рендер сегмента
  const renderSegment = (segment: SegmentFormData, index: number) => {
    const calcInfo = segment.transportType === 'RAILWAY' && segment.pricePerKg && segment.pricePerM3
      ? getCalculationInfo(segment)
      : null;

    return (
      <Card 
        key={segment.key} 
        size="small" 
        style={{ marginBottom: 12 }}
        title={
          <Space>
            {getTransportIcon(segment.transportType)}
            <Text>Сегмент {index + 1}: {segment.transportType === 'AUTO' ? 'Авто' : segment.transportType === 'RAILWAY' ? 'ЖД' : 'Авиа'}</Text>
          </Space>
        }
        extra={
          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeSegment(segment.key)} />
        }
      >
        <Row gutter={12}>
          <Col span={6}>
            <Form.Item label="Тип транспорта" style={{ marginBottom: 8 }}>
              <Select
                value={segment.transportType}
                onChange={(v) => updateSegment(segment.key, 'transportType', v)}
              >
                <Option value="AUTO"><CarOutlined /> Авто</Option>
                <Option value="RAILWAY"><NodeIndexOutlined /> ЖД</Option>
                <Option value="AIR"><RocketOutlined /> Авиа</Option>
              </Select>
            </Form.Item>
          </Col>
          
          {/* Для ЖД - выпадающие списки из тарифов */}
          {segment.transportType === 'RAILWAY' ? (
            <>
              <Col span={9}>
                <Form.Item label="Точка погрузки" style={{ marginBottom: 8 }}>
                  <Select
                    value={segment.fromPoint}
                    onChange={(v) => updateSegment(segment.key, 'fromPoint', v)}
                    placeholder="Выберите станцию"
                    showSearch
                    optionFilterProp="children"
                    allowClear
                  >
                    {allStations.map(station => (
                      <Option key={station} value={station}>{station}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={9}>
                <Form.Item label="Точка выгрузки" style={{ marginBottom: 8 }}>
                  <Select
                    value={segment.toPoint}
                    onChange={(v) => updateSegment(segment.key, 'toPoint', v)}
                    placeholder="Выберите станцию"
                    showSearch
                    optionFilterProp="children"
                    allowClear
                  >
                    {allStations.map(station => (
                      <Option key={station} value={station}>{station}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </>
          ) : (
            <>
              <Col span={9}>
                <Form.Item label="Точка погрузки" style={{ marginBottom: 8 }}>
                  <Input 
                    placeholder="Откуда" 
                    value={segment.fromPoint}
                    onChange={(e) => updateSegment(segment.key, 'fromPoint', e.target.value)}
                  />
                </Form.Item>
              </Col>
              <Col span={9}>
                <Form.Item label="Точка выгрузки" style={{ marginBottom: 8 }}>
                  <Input 
                    placeholder="Куда" 
                    value={segment.toPoint}
                    onChange={(e) => updateSegment(segment.key, 'toPoint', e.target.value)}
                  />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>

        {segment.transportType === 'RAILWAY' && (
          <>
            <Row gutter={12}>
              <Col span={6}>
                <Form.Item label="Тип вагона" style={{ marginBottom: 8 }}>
                  <Select
                    value={segment.wagonType}
                    onChange={(v) => updateSegment(segment.key, 'wagonType', v)}
                    allowClear
                    placeholder="Выберите"
                  >
                    <Option value="PASSING">Проходящий</Option>
                    <Option value="DETACHED">Отцепной</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Цена за кг (руб.)" style={{ marginBottom: 8 }}>
                  <InputNumber
                    style={{ width: '100%' }}
                    value={segment.pricePerKg}
                    onChange={(v) => updateSegment(segment.key, 'pricePerKg', v)}
                    min={0}
                    precision={2}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Цена за м³ (руб.)" style={{ marginBottom: 8 }}>
                  <InputNumber
                    style={{ width: '100%' }}
                    value={segment.pricePerM3}
                    onChange={(v) => updateSegment(segment.key, 'pricePerM3', v)}
                    min={0}
                    precision={2}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Ставка (руб.)" style={{ marginBottom: 8 }}>
                  <InputNumber
                    style={{ width: '100%' }}
                    value={segment.segmentCost}
                    onChange={(v) => updateSegment(segment.key, 'segmentCost', v || 0)}
                    min={0}
                    precision={2}
                  />
                </Form.Item>
              </Col>
            </Row>
            {/* Информация о расчёте */}
            {calcInfo && (
              <div style={{ marginTop: 4 }}>
                <Tag color={calcInfo.type === 'weight' ? 'blue' : 'green'}>
                  Расчёт {calcInfo.text} ={' '}
                  <MoneyCell value={calcInfo.cost} type="neutral" />
                </Tag>
              </div>
            )}
          </>
        )}

        {(segment.transportType === 'AUTO' || segment.transportType === 'AIR') && (
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item label="Затраты (руб.)" style={{ marginBottom: 8 }}>
                <InputNumber
                  style={{ width: '100%' }}
                  value={segment.segmentCost}
                  onChange={(v) => updateSegment(segment.key, 'segmentCost', v || 0)}
                  min={0}
                  precision={2}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Ставка клиенту (руб.)" style={{ marginBottom: 8 }}>
                <InputNumber
                  style={{ width: '100%' }}
                  value={segment.clientRate}
                  onChange={(v) => updateSegment(segment.key, 'clientRate', v || undefined)}
                  min={0}
                  precision={2}
                  placeholder="= затраты"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Примечание" style={{ marginBottom: 8 }}>
                <Input
                  value={segment.notes}
                  onChange={(e) => updateSegment(segment.key, 'notes', e.target.value)}
                  placeholder="Комментарий"
                />
              </Form.Item>
            </Col>
          </Row>
        )}
      </Card>
    );
  };

  // Колонки для истории расчётов
  const historyColumns = [
    {
      title: 'Дата',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('ru-RU')
    },
    {
      title: 'Груз',
      key: 'cargo',
      render: (_: any, record: any) => (
        <Space direction="vertical" size={0}>
          <Text>{record.cargoPlaces} мест</Text>
          <Text type="secondary">{record.cargoWeightKg} кг / {record.cargoVolumeM3} м³</Text>
        </Space>
      )
    },
    {
      title: 'Маршрут',
      key: 'route',
      width: 350,
      render: (_: any, record: any) => (
        <Space direction="vertical" size={2} style={{ width: '100%' }}>
          {record.segments?.map((s: any, i: number) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Tag icon={getTransportIcon(s.transportType)} style={{ margin: 0 }}>
                {s.fromPoint} → {s.toPoint}
              </Tag>
              <Text type="secondary" style={{ fontSize: 11 }}>
                <MoneyCell value={s.segmentCost} type="neutral" />
              </Text>
              {s.clientRate && s.clientRate !== s.segmentCost && (
                <span style={{ fontSize: 11 }}>
                  {' '}
                  / <MoneyCell value={s.clientRate} type="neutral" style={{ color: '#1890ff' }} />
                </span>
              )}
            </div>
          ))}
          {record.expeditionCost ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Tag color="purple" style={{ margin: 0 }}>Экспедирование</Tag>
              <Text type="secondary" style={{ fontSize: 11 }}>
                <MoneyCell value={record.expeditionCost} type="neutral" />
              </Text>
            </div>
          ) : null}
        </Space>
      )
    },
    {
      title: 'Ставка',
      dataIndex: 'totalCost',
      key: 'totalCost',
      render: (v: number) => (
        <Text strong>
          <MoneyCell value={v} type="income" />
        </Text>
      )
    },
    {
      title: 'Ставка клиенту',
      dataIndex: 'totalClientRate',
      key: 'totalClientRate',
      render: (v: number) =>
        v ? (
          <Text strong>
            <MoneyCell value={v} type="neutral" style={{ color: '#1890ff' }} />
          </Text>
        ) : (
          '—'
        )
    },
    {
      title: 'Клиент',
      dataIndex: ['client', 'name'],
      key: 'client',
      render: (name: string) => name || '—'
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 100,
      render: (_: any, record: DeliveryCostCalculation) => (
        <Button
          type="link"
          size="small"
          icon={<InfoCircleOutlined />}
          onClick={() => {
            setSelectedCalculation(record);
            // Если сегменты не загружены, загружаем детальные данные
            if (!record.segments || record.segments.length === 0) {
              setSelectedCalculationId(record.id);
            }
            setDetailModalVisible(true);
          }}
        >
          Детали
        </Button>
      )
    }
  ];

  // Колонки для тарифов ЖД
  const tariffColumns = [
    { title: 'Откуда', dataIndex: 'fromStation', key: 'fromStation' },
    { title: 'Куда', dataIndex: 'toStation', key: 'toStation' },
    { 
      title: 'Тип вагона', 
      dataIndex: 'wagonType', 
      key: 'wagonType',
      render: (v: string) => v === 'PASSING' ? 'Проходящий' : v === 'DETACHED' ? 'Отцепной' : '—'
    },
    { 
      title: 'Мин. вес (кг)', 
      dataIndex: 'minWeightKg', 
      key: 'minWeightKg',
      render: (v: number) => v ? `${v} кг` : '—'
    },
    { title: 'Цена/кг', dataIndex: 'pricePerKg', key: 'pricePerKg', render: (v: number) => `${v} ₽` },
    { title: 'Цена/м³', dataIndex: 'pricePerM3', key: 'pricePerM3', render: (v: number) => `${v} ₽` },
    {
      title: 'Действия',
      key: 'actions',
      render: (_: any, record: RailwayTariff) => (
        <Space>
          <Button size="small" onClick={() => {
            setEditingTariff(record);
            tariffForm.setFieldsValue(record);
            setTariffModalVisible(true);
          }}>Изменить</Button>
          <Button size="small" danger onClick={() => deleteRailwayTariff(record.id)}>Удалить</Button>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            <CalculatorOutlined /> Расчёт стоимости доставки
          </Title>
          <Text type="secondary">Калькулятор стоимости перевозки грузов</Text>
        </Col>
        <Col>
          <Space>
            <Button icon={<SettingOutlined />} onClick={() => setActiveTab('tariffs')}>
              Тарифы
            </Button>
            <Button icon={<HistoryOutlined />} onClick={() => setActiveTab('history')}>
              История
            </Button>
          </Space>
        </Col>
      </Row>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        {/* КАЛЬКУЛЯТОР */}
        <Tabs.TabPane 
          tab={
            <span>
              Калькулятор
              {editingCalculationId && (
                <Tag color="orange" style={{ marginLeft: 8 }}>Редактирование</Tag>
              )}
            </span>
          } 
          key="calculator"
        >
          <Row gutter={24}>
            {/* Левая колонка - параметры груза и маршрут */}
            <Col span={16}>
              {/* Параметры груза */}
              <Card title="Параметры груза" size="small" style={{ marginBottom: 16 }}>
                <Form form={form} layout="vertical" onValuesChange={(changedValues, allValues) => {
                  // Автоматический расчет объема при изменении габаритов
                  if (changedValues.cargoLengthM !== undefined || changedValues.cargoWidthM !== undefined || changedValues.cargoHeightM !== undefined) {
                    const length = Number(allValues.cargoLengthM) || 0;
                    const width = Number(allValues.cargoWidthM) || 0;
                    const height = Number(allValues.cargoHeightM) || 0;
                    
                    if (length > 0 && width > 0 && height > 0) {
                      const calculatedVolume = length * width * height;
                      form.setFieldsValue({ cargoVolumeM3: Number(calculatedVolume.toFixed(3)) });
                    }
                  }
                  
                  // Пересчет сегментов при изменении веса/объема
                  recalculateSegments();
                }}>
                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item label="Заявка (для автозаполнения)">
                        <Select
                          placeholder="Выберите заявку для автозаполнения данных"
                          allowClear
                          showSearch
                          optionFilterProp="children"
                          value={selectedApplicationId}
                          onChange={(value) => {
                            setSelectedApplicationId(value);
                            if (!value) {
                              // Очищаем поля при сбросе выбора
                              form.setFieldsValue({ clientId: undefined });
                            }
                          }}
                        >
                          {applicationsData?.applications?.map((app: any) => {
                            const label = formatApplicationSelectOptionLabel(app);
                            return (
                            <Option key={app.id} value={app.id}>
                              {label}
                            </Option>
                            );
                          })}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={6}>
                      <Form.Item name="cargoPlaces" label="Количество мест">
                        <InputNumber style={{ width: '100%' }} min={1} placeholder="1" />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="cargoWeightKg" label="Вес (кг)" rules={[{ required: true }]}>
                        <InputNumber style={{ width: '100%' }} min={0} precision={2} />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item 
                        name="cargoVolumeM3" 
                        label="Объём (м³)" 
                        rules={[{ required: true }]}
                        tooltip="Автоматически рассчитывается из габаритов (длина × ширина × высота)"
                      >
                        <InputNumber style={{ width: '100%' }} min={0} precision={3} />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="clientId" label="Клиент">
                        <Select placeholder="Выберите клиента" allowClear showSearch optionFilterProp="children">
                          {clients.map((c: any) => (
                            <Option key={c.id} value={c.id}>{c.name}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={6}>
                      <Form.Item name="cargoLengthM" label="Длина (м)">
                        <InputNumber style={{ width: '100%' }} min={0} precision={3} />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="cargoWidthM" label="Ширина (м)">
                        <InputNumber style={{ width: '100%' }} min={0} precision={3} />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="cargoHeightM" label="Высота (м)">
                        <InputNumber style={{ width: '100%' }} min={0} precision={3} />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item name="cargoDescription" label="Описание груза">
                        <Input placeholder="Наименование" />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
              </Card>

              {/* Сегменты маршрута */}
              <Card 
                title="Маршрут" 
                size="small"
                extra={
                  <Button type="primary" icon={<PlusOutlined />} onClick={addSegment}>
                    Добавить сегмент
                  </Button>
                }
              >
                {segments.length === 0 ? (
                  <Empty description="Добавьте сегменты маршрута" />
                ) : (
                  segments.map((segment, index) => renderSegment(segment, index))
                )}
              </Card>
            </Col>

            {/* Правая колонка - экспедирование и итоги */}
            <Col span={8}>
              {/* Экспедирование */}
              <Card title="Экспедирование" size="small" style={{ marginBottom: 16 }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Space>
                    <Switch checked={includeExpedition} onChange={setIncludeExpedition} />
                    <Text>Включить экспедирование</Text>
                  </Space>
                  
                  {includeExpedition && (
                    <>
                      <Text type="secondary">
                        Тариф по весу: <MoneyCell value={calculatedExpeditionCost} type="neutral" />
                      </Text>
                      <InputNumber
                        style={{ width: '100%' }}
                        placeholder="Своя сумма (если отличается)"
                        value={customExpeditionCost}
                        onChange={(v) => setCustomExpeditionCost(v)}
                        min={0}
                        precision={2}
                        addonBefore="Своя сумма"
                      />
                    </>
                  )}
                </Space>
              </Card>

              {/* Итоги */}
              <Card title="Итого" size="small" style={{ marginBottom: 16 }}>
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                  {/* Расшифровка по сегментам */}
                  {segments.map((s, i) => (
                    <Row key={s.key} justify="space-between">
                      <Col>
                        <Space>
                          {getTransportIcon(s.transportType)}
                          <Text>{s.fromPoint || '?'} → {s.toPoint || '?'}</Text>
                        </Space>
                      </Col>
                      <Col>
                        <Text strong>
                          <MoneyCell value={s.segmentCost} type="income" />
                        </Text>
                      </Col>
                    </Row>
                  ))}
                  
                  {includeExpedition && (
                    <Row justify="space-between">
                      <Col><Text>Экспедирование</Text></Col>
                      <Col>
                        <Text strong>
                          <MoneyCell value={customExpeditionCost || calculatedExpeditionCost} type="expense" />
                        </Text>
                      </Col>
                    </Row>
                  )}

                  <Divider style={{ margin: '12px 0' }} />

                  <Row justify="space-between">
                    <Col><Text strong style={{ fontSize: 16 }}>Общая стоимость:</Text></Col>
                    <Col>
                      <Text strong style={{ fontSize: 18 }}>
                        <MoneyCell value={totalCost} type="income" />
                      </Text>
                    </Col>
                  </Row>

                  <Row justify="space-between">
                    <Col><Text>Ставка клиенту:</Text></Col>
                    <Col>
                      <Text strong>
                        <MoneyCell value={totalClientRate} type="neutral" style={{ color: '#1890ff' }} />
                      </Text>
                    </Col>
                  </Row>

                  {editingCalculationId && (
                    <Button 
                      block 
                      size="large"
                      onClick={() => {
                        setEditingCalculationId(undefined);
                        form.resetFields();
                        setSegments([]);
                        setIncludeExpedition(false);
                        setCustomExpeditionCost(null);
                        setSelectedApplicationId(undefined);
                      }}
                      style={{ marginBottom: 8 }}
                    >
                      Отменить редактирование
                    </Button>
                  )}
                  <Button 
                    type="primary" 
                    icon={<SaveOutlined />} 
                    block 
                    size="large"
                    onClick={handleSaveCalculation}
                    loading={isCreating || isUpdating}
                  >
                    {editingCalculationId ? 'Обновить расчёт' : 'Сохранить расчёт'}
                  </Button>
                </Space>
              </Card>
            </Col>
          </Row>
        </Tabs.TabPane>

        {/* ТАРИФЫ */}
        <Tabs.TabPane tab="Тарифы" key="tariffs">
          <Row gutter={24}>
            <Col span={16}>
              <Card 
                title="Тарифы ЖД перевозок" 
                extra={
                  <Space>
                    <Button onClick={handleSeedTariffs} loading={isSeeding}>Загрузить базовые</Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                      setEditingTariff(null);
                      tariffForm.resetFields();
                      setTariffModalVisible(true);
                    }}>
                      Добавить
                    </Button>
                  </Space>
                }
              >
                <DataTable
                  columns={tariffColumns}
                  dataSource={railwayTariffs}
                  rowKey="id"
                  loading={isLoadingTariffs}
                  size="small"
                  pagination={false}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card 
                title="Тарифы экспедирования"
                extra={
                  <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => {
                    setEditingExpedition(null);
                    expeditionForm.resetFields();
                    setExpeditionModalVisible(true);
                  }}>
                    Добавить
                  </Button>
                }
              >
                <DataTable
                  columns={[
                    { title: 'От (кг)', dataIndex: 'minWeightKg', key: 'min' },
                    { title: 'До (кг)', dataIndex: 'maxWeightKg', key: 'max', render: (v: number) => v || '∞' },
                    { title: 'Цена', dataIndex: 'price', key: 'price', render: (v: number) => <MoneyCell value={v} type="neutral" /> },
                    {
                      title: '',
                      key: 'actions',
                      render: (_: any, record: ExpeditionTariff) => (
                        <Button size="small" onClick={() => {
                          setEditingExpedition(record);
                          expeditionForm.setFieldsValue(record);
                          setExpeditionModalVisible(true);
                        }}>✏️</Button>
                      )
                    }
                  ]}
                  dataSource={expeditionTariffs}
                  rowKey="id"
                  loading={isLoadingExpedition}
                  size="small"
                  pagination={false}
                />
              </Card>
            </Col>
          </Row>
        </Tabs.TabPane>

        {/* ИСТОРИЯ */}
        <Tabs.TabPane tab="История расчётов" key="history">
          <Card>
            {isLoadingCalculations ? (
              <Spin />
            ) : (
              <DataTable
                columns={historyColumns}
                dataSource={calculationsData?.calculations || []}
                rowKey="id"
                pagination={{
                  total: calculationsData?.pagination?.total || 0,
                  pageSize: 20,
                  showSizeChanger: false
                }}
              />
            )}
          </Card>
        </Tabs.TabPane>
      </Tabs>

      {/* Модалка редактирования ЖД тарифа */}
      <Modal
        title={editingTariff ? 'Редактировать тариф ЖД' : 'Новый тариф ЖД'}
        open={tariffModalVisible}
        onOk={handleSaveTariff}
        onCancel={() => setTariffModalVisible(false)}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Form form={tariffForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="fromStation" label="Станция отправления" rules={[{ required: true }]}>
                <Input placeholder="Новосибирск" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="toStation" label="Станция назначения" rules={[{ required: true }]}>
                <Input placeholder="Эльга" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="wagonType" label="Тип вагона">
                <Select allowClear placeholder="Не указан">
                  <Option value="PASSING">Проходящий</Option>
                  <Option value="DETACHED">Отцепной</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="minWeightKg" label="Мин. вес (кг)">
                <InputNumber style={{ width: '100%' }} min={0} placeholder="3000" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="pricePerKg" label="Цена за кг (руб.)" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} precision={2} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="pricePerM3" label="Цена за м³ (руб.)" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} precision={2} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label="Примечание">
            <TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Модалка редактирования тарифа экспедирования */}
      <Modal
        title={editingExpedition ? 'Редактировать тариф экспедирования' : 'Новый тариф экспедирования'}
        open={expeditionModalVisible}
        onOk={handleSaveExpeditionTariff}
        onCancel={() => setExpeditionModalVisible(false)}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Form form={expeditionForm} layout="vertical">
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="minWeightKg" label="От (кг)" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="maxWeightKg" label="До (кг)">
                <InputNumber style={{ width: '100%' }} min={0} placeholder="∞" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="price" label="Цена (руб.)" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} precision={2} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Модалка детальной информации о расчёте */}
      <Modal
        title="Детализация расчёта"
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedCalculation(null);
          setSelectedCalculationId(undefined);
        }}
        footer={[
          <Button key="edit" type="primary" icon={<EditOutlined />} onClick={() => {
            const calc = detailedCalculation || selectedCalculation!;
            setEditingCalculationId(calc.id);
            setDetailModalVisible(false);
            setSelectedCalculation(null);
            setSelectedCalculationId(undefined);
          }}>
            Редактировать
          </Button>,
          <Button key="close" onClick={() => {
            setDetailModalVisible(false);
            setSelectedCalculation(null);
            setSelectedCalculationId(undefined);
          }}>
            Закрыть
          </Button>
        ]}
        width={800}
      >
        {(detailedCalculation || selectedCalculation) && (() => {
          const calc = detailedCalculation || selectedCalculation!;
          return (
            <div>
              {/* Информация о грузе */}
              <Card size="small" style={{ marginBottom: 16 }}>
                <Title level={5}>Параметры груза</Title>
                <Row gutter={16}>
                  <Col span={6}>
                    <Statistic title="Количество мест" value={calc.cargoPlaces} />
                  </Col>
                  <Col span={6}>
                    <Statistic title="Вес" value={calc.cargoWeightKg} suffix="кг" />
                  </Col>
                  <Col span={6}>
                    <Statistic title="Объём" value={calc.cargoVolumeM3} suffix="м³" />
                  </Col>
                  <Col span={6}>
                    <Statistic title="Клиент" value={calc.client?.name || '—'} />
                  </Col>
                </Row>
                {calc.cargoDescription && (
                  <div style={{ marginTop: 12 }}>
                    <Text type="secondary">Описание: {calc.cargoDescription}</Text>
                  </div>
                )}
              </Card>

              {/* Детализация по сегментам */}
              <Card size="small" style={{ marginBottom: 16 }}>
                <Title level={5}>Детализация по маршруту</Title>
                {calc.segments && calc.segments.length > 0 ? (
                  <DataTable
                    columns={[
                      {
                        title: 'Тип',
                        key: 'type',
                        width: 80,
                        render: (_: any, record: any) => getTransportIcon(record.transportType)
                      },
                      {
                        title: 'Маршрут',
                        key: 'route',
                        render: (_: any, record: any) => (
                          <Text>
                            {record.fromPoint} → {record.toPoint}
                        {record.wagonType && (
                          <Tag style={{ marginLeft: 8, fontSize: 11 }}>
                            {record.wagonType === 'PASSING' ? 'Проходящий' : 'Отцепной'}
                          </Tag>
                        )}
                          </Text>
                        )
                      },
                      {
                        title: 'Тариф',
                        key: 'tariff',
                        render: (_: any, record: any) => (
                          <Space direction="vertical" size={0}>
                            {record.pricePerKg && (
                              <Text type="secondary" style={{ fontSize: 11 }}>
                                {record.pricePerKg} ₽/кг
                              </Text>
                            )}
                            {record.pricePerM3 && (
                              <Text type="secondary" style={{ fontSize: 11 }}>
                                {record.pricePerM3} ₽/м³
                              </Text>
                            )}
                          </Space>
                        )
                      },
                      {
                        title: 'Ставка',
                        key: 'cost',
                        align: 'right' as const,
                        render: (_: any, record: any) => (
                          <Text strong>
                            <MoneyCell value={record.segmentCost} type="income" />
                          </Text>
                        )
                      },
                      {
                        title: 'Ставка клиенту',
                        key: 'clientRate',
                        align: 'right' as const,
                        render: (_: any, record: any) => (
                          <Text strong>
                            <MoneyCell
                              value={record.clientRate || record.segmentCost}
                              type="neutral"
                              style={{ color: '#1890ff' }}
                            />
                          </Text>
                        )
                      }
                    ]}
                    dataSource={calc.segments}
                    rowKey={(record, index) => `segment-${index}`}
                    pagination={false}
                    size="small"
                  />
                ) : (
                  <Empty description="Нет данных о сегментах" />
                )}
              </Card>

              {/* Экспедирование */}
              {calc.expeditionCost && (
                <Card size="small" style={{ marginBottom: 16 }}>
                  <Row justify="space-between" align="middle">
                    <Col>
                      <Space>
                        <Tag color="purple">Экспедирование</Tag>
                      </Space>
                    </Col>
                    <Col>
                      <Text strong>
                        <MoneyCell value={calc.expeditionCost} type="neutral" style={{ color: '#722ed1' }} />
                      </Text>
                    </Col>
                  </Row>
                </Card>
              )}

              {/* Итоги */}
              <Card size="small">
                <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
                  <Col>
                    <Text strong style={{ fontSize: 16 }}>Общая стоимость:</Text>
                  </Col>
                  <Col>
                    <Text strong style={{ fontSize: 18 }}>
                      <MoneyCell value={calc.totalCost} type="income" />
                    </Text>
                  </Col>
                </Row>
                <Row justify="space-between" align="middle">
                  <Col>
                    <Text strong style={{ fontSize: 16 }}>Ставка клиенту:</Text>
                  </Col>
                  <Col>
                    <Text strong style={{ fontSize: 18 }}>
                      <MoneyCell
                        value={calc.totalClientRate || calc.totalCost}
                        type="neutral"
                        style={{ color: '#1890ff' }}
                      />
                    </Text>
                  </Col>
                </Row>
              </Card>

              {/* Дата создания */}
              <div style={{ marginTop: 16, textAlign: 'right' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Создано: {new Date(calc.createdAt).toLocaleString('ru-RU')}
                </Text>
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};

export default DeliveryCostCalculatorPage;

